import React, { PropsWithChildren } from 'react';
import styled from 'styled-components';
import { T } from 'src/shared/helpers';
import { useInfiniteScroll } from 'src/shared/hooks';
import { CatalogListItem } from '.';
import { Columns } from '../columns';
import { EmptyList } from '../emptyList';
import { ZTooltip } from '../ztooltip';
import { CatalogListItemLoader } from './catalogListItemLoader';
import { CatalogListItemButton, CatalogListItemModel } from './catalogListItemModel';

export interface CatalogListContentProps<T> {
    loadNext: () => void;
    hasNext: boolean;
    isLoadingNext: boolean;
    readonly models: T[];
    onItemClick: (model: T, hideAlreadyConfiguredBadge?: boolean) => void;
    scrollContainer?: HTMLElement | null;
    noItemsIcon?: React.ReactNode | null;
    noItemsMessage?: string;
    noItemsSubMessage?: React.ReactNode;
    className?: string;
    onCatalogListItemButtonClick?: (button: CatalogListItemButton, model: T) => void;
    columns?: { xs: number; md: number; lg: number };
    hideAlreadyConfiguredBadge?: boolean;
    disableProductCode?: string;
    disabledIds?: Set<string>;
    disabledTooltip?: string;
    tooltipHtml?: string;
    isDamProductsCatalog?: boolean;
    noItemsHideIcon?: boolean;
    /** When true, the root fills the parent height so the empty state stretches to the bottom. */
    fillHeight?: boolean;
    /** Extra node rendered as the last cell of the grid, after all models (e.g. a promo/coming-soon tile). */
    trailing?: React.ReactNode;
}

const CatalogContentRoot = styled.div<{ $fill?: boolean }>`
    ${(props) =>
        props.$fill &&
        `
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
    `}
`;

const FillCenter = styled.div<{ $fill?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;

    /* When filling the height, anchor the empty state near the top (still horizontally centred). */
    ${(props) =>
        props.$fill &&
        `
        flex: 1;
        flex-direction: column;
        justify-content: flex-start;
        padding-top: 8px;
    `}
`;

export const CatalogListContent = <T extends CatalogListItemModel = CatalogListItemModel>({
    loadNext,
    hasNext,
    isLoadingNext,
    models,
    onItemClick,
    scrollContainer,
    noItemsIcon,
    noItemsMessage,
    noItemsSubMessage,
    className,
    columns,
    onCatalogListItemButtonClick,
    hideAlreadyConfiguredBadge,
    disableProductCode,
    disabledIds,
    disabledTooltip,
    tooltipHtml,
    isDamProductsCatalog,
    noItemsHideIcon,
    fillHeight,
    trailing
}: PropsWithChildren<CatalogListContentProps<T>>) => {
    useInfiniteScroll(loadNext, hasNext, scrollContainer, isLoadingNext);

    return (
        <CatalogContentRoot className={className} $fill={fillHeight}>
            {models.length == 0 && !isLoadingNext && (
                <FillCenter $fill={fillHeight}>
                    <EmptyList
                        icon={noItemsIcon}
                        hideIcon={noItemsHideIcon}
                        title={noItemsMessage || T._('Nothing found.', 'Admin')}
                        subTitle={noItemsSubMessage}
                    />
                </FillCenter>
            )}

            {(models.length > 0 || isLoadingNext) && (
                <Columns
                    $xs={columns?.xs ?? 2}
                    $md={columns?.md ?? 5}
                    $lg={columns?.lg ?? 5}
                    $gap={30}
                    css={`
                        margin-bottom: 30px;
                    `}
                >
                    {models.map((model) => {
                        const isDisabled = disabledIds?.has(model.id);
                        const item = (
                            <CatalogListItem
                                selected={model.selected}
                                key={model.id.toString()}
                                model={model}
                                onClick={(hideAlreadyConfiguredBadge) => onItemClick(model, hideAlreadyConfiguredBadge)}
                                onCatalogListItemButtonClick={(button) => onCatalogListItemButtonClick?.(button, model)}
                                hideAlreadyConfiguredBadge={hideAlreadyConfiguredBadge}
                                disableProductCode={isDisabled ? model.id : disableProductCode}
                            />
                        );

                        if (isDisabled && disabledTooltip) {
                            return (
                                <ZTooltip key={model.id.toString()} html={<span>{disabledTooltip}</span>} position={'top'} addContainer>
                                    {item}
                                </ZTooltip>
                            );
                        }

                        return item;
                    })}
                    {isLoadingNext && new Array(10).fill(0).map((_, idx) => <CatalogListItemLoader key={`catalog-list-loader-${idx}`} />)}
                    {!hasNext && !isLoadingNext && trailing}
                </Columns>
            )}
        </CatalogContentRoot>
    );
};
